/**
 * @file usePickList.ts
 * @description Списки для выбора «вручную» в элементах секции (phase19.16
 * Р2; uiux-design-r2 §4): товары, категории каталога, статьи блога, отзывы
 * о товарах и о магазине. Грузятся по требованию, один раз за сеанс
 * (кэш в модуле — панель открывается и закрывается часто).
 *
 * Реестр загрузчиков (OCP): новый список — новая запись.
 *
 * Р3в (v0.19.64): страницы сайта и рубрики блога — для цели переадресации
 * («Сайт → SEO → Переадресации», окно удаления страницы SB128).
 */

import { useEffect, useState } from "react";

import { catalogCategories, catalogProducts } from "../../lib/api-catalog";
import { contentArticles, contentCategories } from "../../lib/api-content";
import { productReviews } from "../../lib/api-product-reviews";
import { sitePages, siteReviews } from "../../lib/api-site-pages";

export interface PickOption {
  id: string;
  label: string;
  description: string;
}

export type PickListKey = "products" | "categories" | "articles" | "productReviews" | "siteReviews" | "sitePages" | "contentCategories";

const LOADERS: Readonly<Record<PickListKey, () => Promise<PickOption[]>>> = {
  products: async () =>
    (await catalogProducts.list())
      .filter((p) => p.status === "ACTIVE" && !p.archivedAt)
      .map((p) => ({ id: p.id, label: p.displayName, description: p.isPublishedOnSite === false ? "не на сайте" : "" })),
  categories: async () =>
    (await catalogCategories.list())
      .filter((c) => !c.isSystemRoot && !c.isExternal)
      .map((c) => ({ id: c.id, label: c.displayName, description: c.isActive ? "" : "скрыта" })),
  articles: async () =>
    (await contentArticles.list({ status: "PUBLISHED" })).map((a) => ({ id: a.id, label: a.title, description: a.categoryName })),
  productReviews: async () =>
    (await productReviews.list({ status: "PUBLISHED", limit: 100 })).items.map((r) => ({
      id: r.id,
      label: `${"★".repeat(r.rating)} ${r.author.name} · ${r.product.name}`,
      description: r.body.slice(0, 80),
    })),
  siteReviews: async () =>
    (await siteReviews.list()).filter((r) => r.isActive).map((r) => ({ id: r.id, label: r.title || r.author, description: r.body.slice(0, 80) })),
  // Страницы с адресом: места и шаблоны адреса не имеют.
  sitePages: async () =>
    (await sitePages.list())
      .filter((p) => (p.kind === "CUSTOM" || p.kind === "SYSTEM_HOME") && !p.deletedAt)
      .map((p) => ({
        id: p.id,
        label: p.title,
        description: `${p.kind === "SYSTEM_HOME" ? "/" : `/${p.liveSlug ?? p.slug}`}${p.isPublished ? "" : " · скрыта"}`,
      })),
  contentCategories: async () =>
    (await contentCategories.list()).map((c) => ({ id: c.id, label: c.name, description: `/blog/category/${c.slug}` })),
};

const cache = new Map<PickListKey, Promise<PickOption[]>>();

/** Сбросить кэш (тесты). */
export function resetPickListCache(): void {
  cache.clear();
}

/** Список устарел (создали или удалили страницу, рубрику) — следующий выбор спросит заново. */
export function invalidatePickList(key: PickListKey): void {
  cache.delete(key);
}

export function usePickList(key: PickListKey | null): { options: PickOption[] | null; failed: boolean } {
  const [state, setState] = useState<{ key: PickListKey | null; options: PickOption[] | null; failed: boolean }>({ key: null, options: null, failed: false });
  useEffect(() => {
    if (!key) return;
    let alive = true;
    let promise = cache.get(key);
    if (!promise) {
      promise = LOADERS[key]();
      cache.set(key, promise);
      promise.catch(() => cache.delete(key));
    }
    promise
      .then((options) => alive && setState({ key, options, failed: false }))
      .catch(() => alive && setState({ key, options: null, failed: true }));
    return () => {
      alive = false;
    };
  }, [key]);
  return state.key === key ? { options: state.options, failed: state.failed } : { options: null, failed: false };
}
